import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Calendar, Scale, Activity, Mic, 
  CheckCircle2, Eye, EyeOff, ArrowRight, Pill, Moon, Sun,
  User, BarChart3, MessageSquare, Camera, LogOut
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import { publicAPI } from '../api';

// Simple client auth without full context
const useClientAuth = (accessToken) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem(`client_auth_${accessToken}`);
    if (savedToken === 'authenticated') {
      loadClientData();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  const loadClientData = async () => {
    try {
      const [clientRes, progressRes] = await Promise.all([
        publicAPI.getClient(accessToken),
        publicAPI.getProgress(accessToken),
      ]);
      setClientData({ ...clientRes.data, progress: progressRes.data });
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Error loading client data:', err);
      localStorage.removeItem(`client_auth_${accessToken}`);
    } finally {
      setLoading(false);
    }
  };

  const login = async (pin) => {
    // Simple PIN validation (last 4 chars of access token)
    const expectedPin = accessToken.slice(-4).toUpperCase();
    if (pin.toUpperCase() === expectedPin) {
      localStorage.setItem(`client_auth_${accessToken}`, 'authenticated');
      await loadClientData();
      return true;
    }
    return false;
  };

  const logout = () => {
    localStorage.removeItem(`client_auth_${accessToken}`);
    setIsAuthenticated(false);
    setClientData(null);
  };

  return { isAuthenticated, clientData, loading, login, logout };
};

// Vitamin display (x100)
const formatVitamins = (value) => ((value || 0) * 100).toLocaleString();

const ClientPortal = () => {
  const { accessToken } = useParams();
  const { isAuthenticated, clientData, loading, login, logout } = useClientAuth(accessToken);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('client-theme') === 'dark');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    localStorage.setItem('client-theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-main)' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <ClientLogin accessToken={accessToken} onLogin={login} isDark={isDark} setIsDark={setIsDark} />;
  }

  return (
    <ClientDashboard 
      clientData={clientData} 
      onLogout={logout} 
      isDark={isDark} 
      setIsDark={setIsDark}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

// Client Login Screen
const ClientLogin = ({ accessToken, onLogin, isDark, setIsDark }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const success = await onLogin(pin);
    if (!success) {
      setError('PIN incorrecto. Usa los últimos 4 caracteres de tu código de acceso.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg-main)' }}>
      <div className="w-full max-w-md">
        {/* Theme toggle */}
        <div className="flex justify-end mb-8">
          <button
            onClick={() => setIsDark(!isDark)}
            className="p-3 rounded-xl border transition-colors"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
          >
            {isDark ? <Sun size={20} style={{ color: 'var(--text-secondary)' }} /> : <Moon size={20} style={{ color: 'var(--text-secondary)' }} />}
          </button>
        </div>

        <div className="card-organic text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-[#6B8E7D] to-[#8AA894]">
              <Pill size={40} className="text-white" />
            </div>
          </div>

          <h1 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Portal del Cliente
          </h1>
          <p className="mb-8" style={{ color: 'var(--text-muted)' }}>
            Ingresa tu PIN para ver tu progreso
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-left" style={{ color: 'var(--text-muted)' }}>
                PIN de acceso
              </label>
              <input
                type="text"
                value={pin}
                onChange={(e) => setPin(e.target.value.slice(0, 4))}
                className="input-organic text-center text-2xl tracking-widest"
                placeholder="••••"
                maxLength={4}
                required
                data-testid="client-pin-input"
              />
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Tu PIN son los últimos 4 caracteres de tu enlace de acceso
              </p>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || pin.length < 4}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3"
              data-testid="client-login-btn"
            >
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  Acceder
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <p className="text-xs mt-6" style={{ color: 'var(--text-muted)' }}>
            ¿Problemas para acceder? Contacta a tu coach
          </p>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'var(--text-muted)' }}>
          Powered by <span style={{ color: 'var(--sage-primary)' }}>Antropia</span>
        </p>
      </div>
    </div>
  );
};

// Client Dashboard
const ClientDashboard = ({ clientData, onLogout, isDark, setIsDark, activeTab, setActiveTab }) => {
  const progress = clientData?.progress || {};
  const current = progress.current_stats || {};

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: BarChart3 },
    { id: 'progress', label: 'Progreso', icon: TrendingUp },
    { id: 'consultations', label: 'Consultas', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-main)' }}>
      {/* Header */}
      <header className="border-b" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6B8E7D] to-[#8AA894] flex items-center justify-center text-white font-semibold text-lg">
                {clientData?.name?.charAt(0)}
              </div>
              <div>
                <h1 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                  ¡Hola, {clientData?.name?.split(' ')[0]}!
                </h1>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Coach: {clientData?.coach_name}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsDark(!isDark)}
                className="p-2 rounded-xl border transition-colors"
                style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)' }}
              >
                {isDark ? <Sun size={18} style={{ color: 'var(--text-secondary)' }} /> : <Moon size={18} style={{ color: 'var(--text-secondary)' }} />}
              </button>
              <button
                onClick={onLogout}
                className="p-2 rounded-xl border transition-colors"
                style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)' }}
                data-testid="client-logout-btn"
              >
                <LogOut size={18} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <nav className="flex gap-1 mt-4 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-xl transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-[#6B8E7D] bg-[#6B8E7D]/5'
                    : 'border-transparent'
                }`}
                style={{ color: activeTab === tab.id ? 'var(--sage-primary)' : 'var(--text-muted)' }}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <OverviewTab current={current} progress={progress} />
        )}
        {activeTab === 'progress' && (
          <ProgressTab progress={progress} />
        )}
        {activeTab === 'consultations' && (
          <ConsultationsTab progress={progress} />
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-8 border-t" style={{ borderColor: 'var(--border-color)' }}>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Powered by <span style={{ color: 'var(--sage-primary)' }} className="font-medium">Antropia</span>
        </p>
      </footer>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ current, progress }) => (
  <div className="space-y-6 animate-fade-in">
    {/* Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="card-organic">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(107, 142, 125, 0.15)' }}>
            <Scale size={20} style={{ color: 'var(--sage-primary)' }} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Peso actual</span>
        </div>
        <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          {current.weight_kg || '—'} <span className="text-lg" style={{ color: 'var(--text-muted)' }}>kg</span>
        </p>
      </div>

      <div className="card-organic">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(194, 89, 52, 0.15)' }}>
            <Activity size={20} style={{ color: 'var(--terracotta)' }} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Grasa corporal</span>
        </div>
        <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          {current.body_fat_avg?.toFixed(1) || '—'} <span className="text-lg" style={{ color: 'var(--text-muted)' }}>%</span>
        </p>
      </div>

      <div className="card-organic">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(120, 113, 108, 0.15)' }}>
            <Calendar size={20} style={{ color: 'var(--text-secondary)' }} />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Mediciones</span>
        </div>
        <p className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          {progress.measurements_count || 0}
        </p>
      </div>
    </div>

    {/* Mini Weight Chart */}
    {progress.weight_trend?.length > 1 && (
      <div className="card-organic">
        <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          Tu evolución de peso
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={progress.weight_trend}>
              <defs>
                <linearGradient id="weightGradientClient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6B8E7D" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6B8E7D" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} domain={['auto', 'auto']} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--bg-card)', 
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#6B8E7D" 
                strokeWidth={2}
                fill="url(#weightGradientClient)" 
                name="Peso (kg)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}

    {/* Last Consultation */}
    {progress.last_consultation_summary && (
      <div className="card-organic">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(194, 89, 52, 0.15)' }}>
            <Mic size={20} style={{ color: 'var(--terracotta)' }} />
          </div>
          <h3 className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Última consulta
          </h3>
        </div>
        <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {progress.last_consultation_summary}
        </p>
      </div>
    )}
  </div>
);

// Progress Tab
const ProgressTab = ({ progress }) => (
  <div className="space-y-6 animate-fade-in">
    {progress.weight_trend?.length > 1 ? (
      <>
        <div className="card-organic">
          <h3 className="text-lg font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            Evolución del peso
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={progress.weight_trend}>
                <defs>
                  <linearGradient id="weightGradientFull" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6B8E7D" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6B8E7D" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} domain={['auto', 'auto']} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--bg-card)', 
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#6B8E7D" 
                  strokeWidth={2}
                  fill="url(#weightGradientFull)" 
                  name="Peso (kg)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {progress.body_fat_trend?.length > 1 && (
          <div className="card-organic">
            <h3 className="text-lg font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
              Grasa corporal
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progress.body_fat_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--bg-card)', 
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#C25934" 
                    strokeWidth={2} 
                    name="Grasa corporal (%)" 
                    dot={{ fill: '#C25934' }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </>
    ) : (
      <div className="card-organic empty-state">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--bg-main)' }}>
          <TrendingUp size={28} style={{ color: 'var(--text-muted)' }} />
        </div>
        <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
          Aún no hay suficientes datos
        </h4>
        <p style={{ color: 'var(--text-muted)' }}>
          Las gráficas aparecerán después de tu segunda medición
        </p>
      </div>
    )}
  </div>
);

// Consultations Tab
const ConsultationsTab = ({ progress }) => (
  <div className="space-y-4 animate-fade-in">
    {progress.last_consultation_summary ? (
      <div className="card-organic">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(194, 89, 52, 0.15)' }}>
            <Mic size={24} style={{ color: 'var(--terracotta)' }} />
          </div>
          <div className="flex-1">
            <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              Resumen de tu última consulta
            </h3>
            <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {progress.last_consultation_summary}
            </p>
          </div>
        </div>
      </div>
    ) : (
      <div className="card-organic empty-state">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--bg-main)' }}>
          <MessageSquare size={28} style={{ color: 'var(--text-muted)' }} />
        </div>
        <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
          Sin consultas aún
        </h4>
        <p style={{ color: 'var(--text-muted)' }}>
          Tu coach registrará aquí los resúmenes de tus consultas
        </p>
      </div>
    )}
  </div>
);

export default ClientPortal;
